/* eslint-disable react/jsx-no-undef */
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Footer from 'components/footer/Footer';
import Navbar from 'components/navbar';
import SeoHead from 'components/common/SeoHead';
import { useRouter } from 'next/router';
import { useEffect, useState, SetStateAction, Dispatch } from 'react';
import useResources from '../../hooks/useResources';
import ResourcesApi from 'API/upload-resources';
import { getCookie, setCookie } from 'utils/cookie';
import {
  getLegacyResourceRedirect,
  getResourceTopicGroups,
  ResourceTopic,
  slugify,
} from 'utils/normalize-resource-data';

// ── Slug helpers ───────────────────────────────────────────────────────────
// Slugify: "Computer Studies / I.T." → "computer-studies-it"
// Deslugify: looks up the real value from a list of known options.
const deslugify = (slug: string, options: string[]): string | undefined =>
  options.find((o) => slugify(o) === slug);

// Build a path segment string from the values that are set.
const buildPath = (...parts: (string | undefined)[]) =>
  '/free-resources/' +
  parts
    .filter(Boolean)
    .map((p) => slugify(p as string))
    .join('/');
// ───────────────────────────────────────────────────────────────────────────

const FreeResourcesPage = () => {
  const router = useRouter();

  // Read from the catch-all segments:
  // /free-resources/[subject]/[level]/[examBoard]/[resource]/[revisionType]
  const segments = (router.query.slug as string[] | undefined) ?? [];
  const subject = segments[0] ?? undefined;
  const level = segments[1] ?? undefined;
  const examBoard = segments[2] ?? undefined;
  const resource = segments[3] ?? undefined;
  const revisionType = segments[4] ?? undefined;

  const { resourceData } = useResources();
  useEffect(() => {
    if (!router.isReady || !resourceData) return;
    const parts = (router.query.slug as string[] | undefined) ?? [];
    const legacyRedirect = getLegacyResourceRedirect(parts, resourceData);
    if (legacyRedirect) {
      router.replace(legacyRedirect);
      return;
    }
    let node = resourceData;
    for (let i = 0; i < parts.length; i++) {
      const options = i === 0
        ? node.subject
        : Object.keys(node).filter((key) => key !== 'groups' && key !== 'topic');
      const name = deslugify(parts[i], options);
      if (!name) {
        router.replace('/free-resources');
        return;
      }
      node = node[name];
    }
  }, [router, resourceData]);

  const hasMain = !subject;
  const hasSubject = subject && !level;
  const hasLevel = subject && level && !examBoard;
  const hasExamBoard = subject && level && examBoard && !resource;
  const hasResourceWithoutRevision =
    subject && level && examBoard && resource === 'past-paper' && !revisionType;
  const hasResourceWithRevision =
    subject && level && examBoard && resource === 'revision' && !revisionType;
  const hasRevisionType =
    subject && level && examBoard && resource && revisionType;

  const frTitle = subject
    ? `${subject}${level ? ` ${level}` : ''} Resources | Vital Educators`
    : 'Free GCSE & A-Level Resources | Vital Educators';
  const frDescription = subject
    ? `Free ${subject}${
        level ? ` ${level}` : ''
      } revision materials, past papers, and more on Vital Educators.`
    : 'Access free GCSE and A-level revision resources, past papers, and study materials on Vital Educators.';

  const currentPath = segments.length
    ? '/free-resources/' + segments.join('/')
    : '/free-resources';

  return (
    <div
      style={{
        overflowX: 'hidden',
        height: 'max-content',
        minHeight: '100vh',
      }}
    >
      <SeoHead title={frTitle} description={frDescription} path={currentPath} />
      <Navbar />
      <div
        style={{
          width: '75%',
          margin: 'auto',
        }}
      >
        <div
          style={{
            paddingTop: '20px',
            fontWeight: 500,
          }}
        >
          <Breadcrumb>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => router.replace('/free-resources')}>
                Resources
              </BreadcrumbLink>
            </BreadcrumbItem>
            {subject && (
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => router.push(buildPath(subject))}>
                  {subject}
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
            {level && (
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => router.push(buildPath(subject, level))}
                >
                  {level}
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
            {examBoard && (
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() =>
                    router.push(buildPath(subject, level, examBoard))
                  }
                >
                  {examBoard}
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
            {resource && (
              <BreadcrumbItem
                onClick={() =>
                  router.push(buildPath(subject, level, examBoard, resource))
                }
              >
                <BreadcrumbLink>{resource}</BreadcrumbLink>
              </BreadcrumbItem>
            )}
            {revisionType && (
              <BreadcrumbItem>
                <BreadcrumbLink>{revisionType}</BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </Breadcrumb>
        </div>
        {hasMain && <MainCard />}
        {hasSubject && <LevelCard />}
        {hasLevel && <ExamBoardCard />}
        {hasExamBoard && <ResrouceTypeCard />}
        {hasResourceWithoutRevision && <QuestionCard />}
        {hasResourceWithRevision && <RevisionType />}
        {hasRevisionType && <QuestionCard />}
      </div>
      <Footer />
    </div>
  );
};

const MainCard = () => {
  const router = useRouter();
  const { resourceData } = useResources();
  return (
    <Flex w="full" h="50vh" alignItems={'center'} justifyContent={'center'}>
      <VStack
        w="100%"
        h="60%"
        bg="white"
        shadow={'md'}
        p="1em"
        justifyContent={'space-around'}
      >
        <Heading size="md">Select Subjects</Heading>
        <Divider />
        <Grid templateColumns="repeat(3, 1fr)" width={'full'} gap={3}>
          {resourceData?.subject.map((subject: string, idx: number) => (
            <GridItem w="100%" key={idx}>
              <Button
                bg="#f8f9fa"
                minW={'full'}
                fontWeight={'normal'}
                onClick={() => router.push(buildPath(subject))}
              >
                {subject}
              </Button>
            </GridItem>
          ))}
        </Grid>
      </VStack>
    </Flex>
  );
};

const QuestionCard = () => {
  const router = useRouter();
  const segments = (router.query.slug as string[] | undefined) ?? [];
  const [subjectSlug, levelSlug, examBoardSlug, resourceSlug, revisionSlug] =
    segments as (string | undefined)[];

  const [showForm, setShowForm] = useState(getCookie('subscribed') !== 'true');

  const { resourceData } = useResources();

  // Deslugify to look up data in the resourceData tree
  const subject = subjectSlug
    ? deslugify(subjectSlug, resourceData?.subject ?? [])
    : undefined;
  const level = subject
    ? deslugify(
        levelSlug ?? '',
        Object.keys(resourceData?.[subject as string] ?? {})
      )
    : undefined;
  const examBoard =
    subject && level
      ? deslugify(
          examBoardSlug ?? '',
          Object.keys(
            resourceData?.[subject as string]?.[level as string] ?? {}
          )
        )
      : undefined;
  const resource =
    subject && level && examBoard
      ? deslugify(
          resourceSlug ?? '',
          Object.keys(
            resourceData?.[subject as string]?.[level as string]?.[
              examBoard as string
            ] ?? {}
          )
        )
      : undefined;
  const revisionType =
    revisionSlug && resource
      ? deslugify(
          revisionSlug,
          Object.keys(
            resourceData?.[subject as string]?.[level as string]?.[
              examBoard as string
            ]?.[resource as string] ?? {}
          )
        )
      : undefined;

  const resourceLevel =
    subject && level && examBoard && resource
      ? resourceData?.[subject]?.[level]?.[examBoard]?.[resource]
      : undefined;
  const topics = revisionType ? resourceLevel?.[revisionType] : resourceLevel;
  const topicGroups = getResourceTopicGroups(topics);

  if (!subject || !level || !examBoard || !resource) return null;

  return (
    <Flex
      w="full"
      h="max-content"
      alignItems={'center'}
      justifyContent={'center'}
    >
      <VStack
        w="100%"
        h="max-content"
        bg="white"
        shadow={'md'}
        p="1em"
        justifyContent={'space-around'}
      >
        <Heading size="md">Select Subjects</Heading>
        <Divider />
        <Grid templateColumns="repeat(2, 1fr)" width={'full'} pt="1em" gap={3}>
          {topicGroups.length > 0 ? (
            topicGroups.map((group) => (
              <GridItem w="100%" key={group[0].id}>
                {group.map((s) => (
                  <EmailModal
                    buttonTxt={s.title.replace(/\.pdf$/i, '').replace(/_/g, ' ')}
                    resource={s}
                    showForm={showForm}
                    setShowForm={setShowForm}
                    key={s.id}
                  />
                ))}
              </GridItem>
            ))
          ) : (
            <GridItem w="100%">
              <Flex
                h="10em"
                alignItems={'center'}
                justifyContent={'center'}
                color="gray.500"
              >
                No resources found for this topic.
              </Flex>
            </GridItem>
          )}
        </Grid>
      </VStack>
    </Flex>
  );
};

const LevelCard = () => {
  const router = useRouter();
  const segments = (router.query.slug as string[] | undefined) ?? [];
  const subjectSlug = segments[0];
  const { resourceData } = useResources();

  const subject = subjectSlug
    ? deslugify(subjectSlug, resourceData?.subject ?? [])
    : undefined;

  return (
    <Flex w="full" h="50vh" alignItems={'center'} justifyContent={'center'}>
      <VStack
        w="100%"
        h="40%"
        bg="white"
        shadow={'md'}
        p="1em"
        justifyContent={'space-around'}
      >
        <Heading size="md">Subject Level</Heading>
        <Divider />
        <Flex w="100%" justifyContent={'center'} gap="1em" flexWrap={'wrap'}>
          {resourceData &&
            subject &&
            Object.keys(resourceData[subject]).map((level, idx) => (
              <Button
                key={idx}
                bg="#f8f9fa"
                minW={'10em'}
                fontWeight={'normal'}
                onClick={() => router.push(buildPath(subject, level))}
              >
                {level.replace('_', '/')}
              </Button>
            ))}
        </Flex>
      </VStack>
    </Flex>
  );
};

const ExamBoardCard = () => {
  const router = useRouter();
  const segments = (router.query.slug as string[] | undefined) ?? [];
  const subjectSlug = segments[0];
  const levelSlug = segments[1];
  const { resourceData } = useResources();

  const subject = subjectSlug
    ? deslugify(subjectSlug, resourceData?.subject ?? [])
    : undefined;
  const level = subject
    ? deslugify(levelSlug, Object.keys(resourceData?.[subject] ?? {}))
    : undefined;

  return (
    <Flex w="full" h="50vh" alignItems={'center'} justifyContent={'center'}>
      <VStack
        w="100%"
        h="40%"
        bg="white"
        shadow={'md'}
        p="1em"
        justifyContent={'space-around'}
      >
        <Heading size="md">Exam Board</Heading>
        <Divider />
        <Flex w="100%" justifyContent={'center'} gap="1em" flexWrap={'wrap'}>
          {resourceData &&
            subject &&
            level &&
            Object.keys(resourceData[subject][level]).map((board, idx) => (
              <Button
                key={idx}
                bg="#f8f9fa"
                minW={'10em'}
                fontWeight={'normal'}
                onClick={() => router.push(buildPath(subject, level, board))}
              >
                {board}
              </Button>
            ))}
        </Flex>
      </VStack>
    </Flex>
  );
};

const ResrouceTypeCard = () => {
  const router = useRouter();
  const segments = (router.query.slug as string[] | undefined) ?? [];
  const subjectSlug = segments[0];
  const levelSlug = segments[1];
  const examBoardSlug = segments[2];
  const { resourceData } = useResources();

  const subject = subjectSlug
    ? deslugify(subjectSlug, resourceData?.subject ?? [])
    : undefined;
  const level = subject
    ? deslugify(levelSlug, Object.keys(resourceData?.[subject] ?? {}))
    : undefined;
  const examBoard =
    subject && level
      ? deslugify(
          examBoardSlug ?? '',
          Object.keys(resourceData?.[subject]?.[level] ?? {})
        )
      : undefined;

  return (
    <Flex w="full" h="50vh" alignItems={'center'} justifyContent={'center'}>
      <VStack
        w="100%"
        h="40%"
        bg="white"
        shadow={'md'}
        p="1em"
        justifyContent={'space-around'}
      >
        <Heading size="md">Resource Type</Heading>
        <Divider />
        <Flex w="100%" justifyContent={'center'} gap="1em">
          {resourceData &&
            subject &&
            level &&
            examBoard &&
            Object.keys(resourceData[subject][level][examBoard]).map(
              (r, idx) => (
                <Button
                  key={idx}
                  bg="#f8f9fa"
                  minW={'10em'}
                  fontWeight={'normal'}
                  onClick={() =>
                    router.push(buildPath(subject, level, examBoard, r))
                  }
                >
                  {r}
                </Button>
              )
            )}
        </Flex>
      </VStack>
    </Flex>
  );
};

const RevisionType = () => {
  const router = useRouter();
  const segments = (router.query.slug as string[] | undefined) ?? [];
  const subjectSlug = segments[0];
  const levelSlug = segments[1];
  const examBoardSlug = segments[2];
  const resourceSlug = segments[3];
  const { resourceData } = useResources();

  const subject = subjectSlug
    ? deslugify(subjectSlug, resourceData?.subject ?? [])
    : undefined;
  const level = subject
    ? deslugify(
        levelSlug ?? '',
        Object.keys(resourceData?.[subject as string] ?? {})
      )
    : undefined;
  const examBoard =
    subject && level
      ? deslugify(
          examBoardSlug ?? '',
          Object.keys(
            resourceData?.[subject as string]?.[level as string] ?? {}
          )
        )
      : undefined;
  const resource =
    subject && level && examBoard
      ? deslugify(
          resourceSlug ?? '',
          Object.keys(
            resourceData?.[subject as string]?.[level as string]?.[
              examBoard as string
            ] ?? {}
          )
        )
      : undefined;

  return (
    <Flex w="full" h="50vh" alignItems={'center'} justifyContent={'center'}>
      <VStack
        w="65%"
        h="40%"
        bg="white"
        shadow={'md'}
        p="1em"
        justifyContent={'space-around'}
      >
        <Heading size="md">Resources Revision Type</Heading>
        <Divider />
        <Flex w="100%" justifyContent={'center'} gap="1em">
          {resourceData &&
            subject &&
            level &&
            examBoard &&
            resource &&
            Object.keys(resourceData[subject][level][examBoard][resource]).map(
              (revisionType, idx) => (
                <Button
                  key={idx}
                  bg="#f8f9fa"
                  minW={'10em'}
                  fontWeight={'normal'}
                  onClick={() =>
                    router.push(
                      buildPath(
                        subject,
                        level,
                        examBoard,
                        resource,
                        revisionType
                      )
                    )
                  }
                >
                  {revisionType}
                </Button>
              )
            )}
        </Flex>
      </VStack>
    </Flex>
  );
};

function EmailModal({
  buttonTxt,
  resource,
  showForm,
  setShowForm,
}: {
  buttonTxt: string;
  resource: ResourceTopic;
  showForm: boolean;
  setShowForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [email, setEmail] = useState('');
  const { requestLinkToEmail } = useResources();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const handleSendEmail = async () => {
    setLoading(true);
    setError('');
    if (!email) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!email.match(/.+\@.+\..+/)) {
      setError('Email is invalid');
      setLoading(false);
      return;
    }
    try {
      const { success, subscribed, signedUrl } = await requestLinkToEmail(
        email,
        resource.id.toString()
      );
      if (!success || !signedUrl) throw new Error('Resource is unavailable');
      if (subscribed) {
        setCookie('subscribed', 'true', 180);
        setShowForm(false);
      }
      openResource(signedUrl);
      toast({
        title: 'Email Sent',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      reset();
    } catch {
      setError('This resource is currently unavailable. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setEmail('');
    setLoading(false);
    setError('');
    onClose();
  };

  const getResource = async () => {
    try {
      const url = await ResourcesApi.getLink(resource.id);
      openResource(url);
    } catch {
      toast({
        title: 'Something went wrong',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const openResource = (url: string) => {
    window.location.assign(url);
  };

  return (
    <>
      <Button
        cursor={'pointer'}
        bg="#f8f9fa"
        minW={'10em'}
        width={['20%', '100%']}
        height={'auto'}
        fontWeight={'normal'}
        fontSize={['13px', '16px']}
        justifyContent={'flex-start'}
        padding={['5px', '10px']}
        onClick={showForm ? onOpen : getResource}
        style={{ textWrap: 'wrap', lineHeight: 'initial' } as any}
      >
        {buttonTxt}
      </Button>

      <Modal isOpen={isOpen} onClose={reset}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter your email to access this resource</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              required={true}
              placeholder="Enter Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && (
              <div style={{ marginTop: '6px', color: 'red' }}>{error}</div>
            )}
            <div style={{ marginTop: '6px', fontStyle: 'italic' }}>
              You'll receive occasional emails regarding updates of these
              resources.
            </div>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={reset}>
              Close
            </Button>
            <Button
              isLoading={loading}
              disabled={loading}
              type="submit"
              onClick={handleSendEmail}
              colorScheme="blue"
            >
              Subscribe
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default FreeResourcesPage;
